import { ThemeProvider as FlowbiteThemeProvider } from "flowbite-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "@/shared/components/theme/ThemeContext";
import { AppDependencies, DependenciesContext } from "@/shared/context/DependenciesContext";
import { AppToaster } from "@/shared/lib/AppToaster";
import { setupGlobalErrorHandler } from "@/shared/lib/global-error-handler";
import { queryClient } from "@/shared/lib/query-client";
import { createErrorHandlerService } from "@/shared/services/error-handler.service";
import { LocalStorageService } from "@/shared/services/localStorage.service";
import { createNotificationService } from "@/shared/services/notification.service";
import { defaultPushService } from "@/shared/services/push.service";
import customTheme from "@/utils/theme";

type AppDependenciesProviderProps = React.PropsWithChildren<{ enableDevtools?: boolean }>;

export const createAppDependencies = (): AppDependencies => {
  const notificationService = createNotificationService();
  const errorHandlerService = createErrorHandlerService(notificationService);

  setupGlobalErrorHandler(queryClient, errorHandlerService);

  return {
    storageService: new LocalStorageService(),
    queryClient,
    pushService: defaultPushService,
    notificationService,
    errorHandlerService,
  };
};

export const AppDependenciesProvider = ({
  children,
  enableDevtools = false,
}: AppDependenciesProviderProps) => {
  const dependencies = createAppDependencies();

  return (
    <DependenciesContext.Provider value={dependencies}>
      <QueryClientProvider client={dependencies.queryClient}>
        <ThemeProvider>
          <FlowbiteThemeProvider theme={customTheme as Record<string, unknown>}>
            {children}
            <AppToaster />
          </FlowbiteThemeProvider>
        </ThemeProvider>
        {enableDevtools && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </DependenciesContext.Provider>
  );
};
